import React from 'react';
import { motion } from 'framer-motion';


import { PageTransition } from '../components/PageTransition';
import { TrainingMetrics } from '../components/TrainingMetrics';
import { ConfusionMatrix } from '../components/ConfusionMatrix';
import { ModelComparisonTable } from '../components/ModelComparisonTable';

export const ModelPerformance: React.FC = () => {
  return (
    <PageTransition className="model-performance-page space-y-8">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 mb-2"
          >
            <div className="p-2 bg-[rgba(0,251,251,0.1)] rounded-xl text-accent-blue border border-[rgba(0,251,251,0.2)]">
              <span className="icon-fallback"  >📈</span>
            </div>
            <h1 className="text-3xl font-bold m-0 text-text-primary">Model Performance</h1>
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-text-secondary max-w-2xl"
          >
            Deep dive into the training metrics, evaluation scores, and comparative analysis of different Graph Neural Network architectures on the Cora dataset.
          </motion.p>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-2 bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.2)] px-4 py-2 rounded-lg text-emerald-400 font-medium text-sm"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          Models fully trained
        </motion.div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Training Metrics — full width */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="xl:col-span-2 glass-card relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent-blue rounded-full blur-[150px] opacity-10 pointer-events-none -translate-y-1/2 translate-x-1/2" />
          <h3 className="flex items-center gap-2 font-bold text-lg mb-6">
            <span className="icon-fallback" style={{ color: '#6bf7fb' }}>📊</span>
            Training Progress
          </h3>
          <TrainingMetrics />
        </motion.div>

        {/* Confusion Matrix */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card"
        >
          <h3 className="flex items-center gap-2 font-bold text-lg mb-6">
            <span className="icon-fallback text-accent-violet" >🔲</span>
            Confusion Matrix
          </h3>
          <p className="text-text-muted text-sm mb-6">
            Visualizing the performance of the selected GNN model across all 7 topic classes in the Cora dataset. Diagonal cells represent correct predictions.
          </p>
          <ConfusionMatrix />
        </motion.div>

        {/* Model Comparison Table */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-card"
        >
          <h3 className="flex items-center gap-2 font-bold text-lg mb-6">
            <span className="icon-fallback text-accent-amber" >⚡</span>
            Architecture Comparison
          </h3>
          <p className="text-text-muted text-sm mb-6">
            Leaderboard comparing different model architectures based on test set accuracy, F1 score, and computational efficiency.
          </p>
          <div className="h-[400px] overflow-hidden">
            <ModelComparisonTable />
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
};
